<template>
  <div :id="chartId" style="overflow: hidden; width: 100%; height: 100%;"></div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';
import zingchart from 'zingchart';
import 'zingchart/modules-es6/zingchart-treemap.min.js';
import { useStore } from 'vuex';
import { mapActions, mapState } from 'vuex';

export default {
  name: 'HeatMap',
  setup() {
    const chartId = ref('zingchart-' + Math.random().toString(36).substr(2, 9));
    const store = useStore();
    const windowWidth = ref(window.innerWidth);

    const heatData = computed(() => store.state.analysis.heatList);
    const heatType = computed(() => store.state.analysis.heatType);

    const formattedHeatData = computed(() => {
      return heatData.value.map((item, index) => {
        const isMobile = windowWidth.value <= 1200;
        let font = isMobile ? 5 : 10;

        if (index < (isMobile ? 2 : 3)) {
          font = isMobile ? 15 : 30;
        } else if (index < (isMobile ? 6 : 6)) {
          font = isMobile ? 7 : 15;
        } else if (index < (isMobile ? 10 : 10)) {
          font = isMobile ? 5 : 10;
        } else if (index < (isMobile ? 15 : 15)) {
          font -= 1;
        } else {
          font -= 3;
        }

        if (heatType.value === 'change') {
          font = isMobile ? 12 : 15;
        }

        let backColor;
        if (item.long) {
          backColor = item.long < item.short ? 'var(--color-red)' : 'var(--color-green)';
        } else if (item.data) {
          backColor = item.data < 0 ? 'var(--color-red)' : 'var(--color-green)';
        } else {
          backColor = 'var(--color-green)';
        }

        return {
          ...item,
          style: {
            fontSize: `${font}px`,
            textAlign: 'center',
            verticalAlign: 'middle',
            backgroundColor: backColor,
          },
        };
      });
    });

    const chartData = ref({
      type: 'treemap',
      backgroundColor: 'transparent',
      gui: { contextMenu: { visible: false } },
      options: {
        splitType: 'balancedV2',
        box: {
          borderColor: 'rgba(45, 45, 45, 0.9)',
          borderRadius: '8px',
          borderWidth: '5px',
          margin: '5px',
          padding: '10px',
          overflow: 'hidden',
          hoverState: 'none'
        },
        tooltipBox: {
          text: '%text',
          overflow: 'hidden'
        },
      },
      plotarea: {
        margin: '5',
      },
      series: formattedHeatData.value,
    });

    const renderChart = () => {
      zingchart.render({
        id: chartId.value,
        data: chartData.value,
        height: '100%',
        width: '100%',
      });

      nextTick(() => {
        zingchart.exec(chartId.value, 'resize');
        removeBtn();
      });
    };

    const removeBtn = async() => {
      nextTick(() => {
        const toolBar = document.querySelector(`#${chartId.value}-tools`);
        const zingBtn = document.querySelectorAll('[id*="license-text"]');
        if (zingBtn.length > 0 && zingBtn[0]) {
          zingBtn[0].style.display = 'none';
        }
        if (toolBar) toolBar.style.display = 'none';
      });
    };

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      renderChart();
      window.addEventListener('resize', handleResize);
      removeBtn();
    });

    onBeforeUnmount(() => {
      zingchart.exec(chartId.value, 'destroy');
      window.removeEventListener('resize', handleResize);
    });

    watch(formattedHeatData, (newData) => {
      chartData.value.series = newData;
      renderChart();
      removeBtn();
    });

    watch(() => window.innerWidth, () => {
      nextTick(() => renderChart());
      removeBtn();
    });

    return { chartId };
  },
  async created() {
    await this.loadHeatMap();
  },
  computed: {
    ...mapState('analysis', ['heatList', 'heatType']),
    ...mapState('dominance', ['lang']),
  },
  methods: {
    ...mapActions('analysis', ['loadHeatMap']),
  },
  watch: {
    heatType() {
      this.loadHeatMap();
    },
    lang() {
      this.loadHeatMap();
    }
  },
};
</script>

<style>
#chartId {
  width: 100% !important;
  height: 100% !important;
}
</style>
